"use client";

import { useMemo, useRef, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SMALL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  EXPRESSION_ERROR_STRINGS,
  expressionErrorMessage,
} from "@/components/tools/calculus/expression-errors";
import { GraphingView } from "@/components/tools/calculus/graphing-view";
import {
  PLOT_HEIGHT,
  PLOT_WIDTH,
} from "@/components/tools/math/coordinate-plane";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { parseRow, type RowResult } from "@/lib/tool-kit/calculus/graph-model";
import { autoWindow, type Window } from "@/lib/tool-kit/calculus/plot";
import { equalizeAspect } from "@/lib/tool-kit/math/plot";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

/** English strings. A translated content file overrides these through `ui`. */
export const GRAPHING_CALCULATOR_STRINGS = {
  rowLabel: "Function {n}: y =",
  rowHint:
    "Use x as the variable: x^2 − 3, sin(x)/x, abs(x), sqrt(x), e^(−x^2), ln(x), 1/x. Functions: sin, cos, tan, sec, csc, cot, asin, acos, atan, sinh, cosh, tanh, exp, ln, log (base 10), sqrt, abs. Constants: pi and e.",
  rowPlaceholder: "x^2 − 3",
  addFunction: "Add a function",
  removeFunction: "Remove function {n}",
  maxFunctions: "Up to {max} functions.",
  windowHeading: "Window",
  xMinLabel: "x min",
  xMaxLabel: "x max",
  yMinLabel: "y min",
  yMaxLabel: "y max",
  autoY: "Fit y to the curves",
  square: "Square grid (1 : 1)",
  zoomIn: "Zoom in",
  zoomOut: "Zoom out",
  panLeft: "← Left",
  panRight: "Right →",
  panUp: "↑ Up",
  panDown: "Down ↓",
  reset: "Reset view",
  features: "Mark on the graph",
  featureZeros: "Zeros (x-intercepts)",
  featureCrossings: "Intersections",
  featureTurning: "Maxima and minima",
  tableStartLabel: "Table starts at x =",
  tableStepLabel: "Step",
  results: "Results",
  graphTitle: "Graph of {count} function(s) on x from {xMin} to {xMax}",
  downloadPng: "Download graph (PNG)",
  downloadSvg: "Download graph (SVG)",
  seriesLabel: "y = {expression}",
  zerosHeading: "Zeros",
  zerosNone: "No zeros found in this window.",
  zerosLine: "Function {n}: x = {values}",
  crossingsHeading: "Intersections",
  crossingsNone: "No intersections found in this window.",
  columnPair: "Functions",
  columnX: "x",
  columnY: "y",
  pairText: "{a} and {b}",
  turningHeading: "Maxima and minima",
  turningNone: "No local maxima or minima found in this window.",
  columnFunction: "Function",
  columnKind: "Type",
  kindMax: "maximum",
  kindMin: "minimum",
  functionName: "Function {n}",
  tableHeading: "Table of values",
  tableColumnX: "x",
  tableColumnFunction: "y{n}",
  undefinedValue: "undefined",
  more: "…and {count} more.",
  noFunctions: "Enter a function to draw it.",
  windowInvalid:
    "Enter the window as numbers with min less than max. The graph is drawn on the default window until then.",
  tableInvalid: "Enter the table's starting x and a nonzero step as numbers.",
  oneVariable: "Use only x as the variable.",
  expressionErrors: EXPRESSION_ERROR_STRINGS,
} as const;

export type GraphingStrings = typeof GRAPHING_CALCULATOR_STRINGS;

interface Row {
  id: number;
  text: string;
}

export const MAX_ROWS = 6;
const DEFAULT_WINDOW = { xMin: -10, xMax: 10, yMin: -5, yMax: 5 };

const fieldText = (value: number): string =>
  String(Number(value.toPrecision(6)));

function rowProblem(
  result: RowResult,
  strings: GraphingStrings
): string | null {
  if (result.kind === "error") {
    return expressionErrorMessage(result.error, strings.expressionErrors);
  }
  return result.kind === "many-variables" ? strings.oneVariable : null;
}

export default function GraphingCalculatorWidget() {
  const strings = useToolUi(GRAPHING_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const nextId = useRef(3);
  const [rows, setRows] = useState<Row[]>([
    { id: 1, text: "sin(x)" },
    { id: 2, text: "x/2" },
  ]);
  const [xMinText, setXMinText] = useState(String(DEFAULT_WINDOW.xMin));
  const [xMaxText, setXMaxText] = useState(String(DEFAULT_WINDOW.xMax));
  const [yMinText, setYMinText] = useState(String(DEFAULT_WINDOW.yMin));
  const [yMaxText, setYMaxText] = useState(String(DEFAULT_WINDOW.yMax));
  const [autoY, setAutoY] = useState(true);
  const [square, setSquare] = useState(false);
  const [zeros, setZeros] = useState(true);
  const [crossings, setCrossings] = useState(true);
  const [turning, setTurning] = useState(false);
  const [tableStart, setTableStart] = useState("-5");
  const [tableStep, setTableStep] = useState("1");
  const parsed = useMemo(
    () => rows.map((row, index) => parseRow(row.text, index)),
    [rows]
  );
  const functions = useMemo(
    () => parsed.flatMap((result) => (result.kind === "ok" ? [result.fn] : [])),
    [parsed]
  );
  const xMin = parseNumberInput(xMinText);
  const xMax = parseNumberInput(xMaxText);
  const typedX = xMin !== null && xMax !== null && xMin < xMax;
  const xRange: [number, number] = typedX
    ? [xMin, xMax]
    : [DEFAULT_WINDOW.xMin, DEFAULT_WINDOW.xMax];
  const yMin = parseNumberInput(yMinText);
  const yMax = parseNumberInput(yMaxText);
  const typedY = yMin !== null && yMax !== null && yMin < yMax;
  const view: Window = useMemo(() => {
    let window: Window;
    if (autoY || !typedY) {
      const curves = functions.map((fn) => fn.f);
      const auto =
        curves.length > 0
          ? autoWindow(curves, xRange[0], xRange[1])
          : {
              xMin: xRange[0],
              xMax: xRange[1],
              yMin: DEFAULT_WINDOW.yMin,
              yMax: DEFAULT_WINDOW.yMax,
            };
      window = auto;
    } else {
      window = {
        xMin: xRange[0],
        xMax: xRange[1],
        yMin: yMin as number,
        yMax: yMax as number,
      };
    }
    return square ? equalizeAspect(window, PLOT_WIDTH, PLOT_HEIGHT) : window;
  }, [functions, xRange[0], xRange[1], autoY, typedY, yMin, yMax, square]);

  const updateRow = (id: number, text: string) =>
    setRows((previous) =>
      previous.map((row) => (row.id === id ? { ...row, text } : row))
    );
  const addRow = () => {
    setRows((previous) => [...previous, { id: nextId.current, text: "" }]);
    nextId.current += 1;
  };
  const removeRow = (id: number) =>
    setRows((previous) => previous.filter((row) => row.id !== id));

  const setWindow = (next: Window) => {
    setXMinText(fieldText(next.xMin));
    setXMaxText(fieldText(next.xMax));
    setYMinText(fieldText(next.yMin));
    setYMaxText(fieldText(next.yMax));
  };
  const zoom = (factor: number) => {
    const cx = (view.xMin + view.xMax) / 2;
    const cy = (view.yMin + view.yMax) / 2;
    const hx = ((view.xMax - view.xMin) / 2) * factor;
    const hy = ((view.yMax - view.yMin) / 2) * factor;
    setWindow({ xMin: cx - hx, xMax: cx + hx, yMin: cy - hy, yMax: cy + hy });
  };
  const pan = (dx: number, dy: number) => {
    const spanX = view.xMax - view.xMin;
    const spanY = view.yMax - view.yMin;
    if (dy !== 0) {
      setAutoY(false);
    }
    setWindow({
      xMin: view.xMin + dx * spanX,
      xMax: view.xMax + dx * spanX,
      yMin: view.yMin + dy * spanY,
      yMax: view.yMax + dy * spanY,
    });
  };
  const reset = () => {
    setWindow(DEFAULT_WINDOW);
    setAutoY(true);
    setSquare(false);
  };
  const shownYMin = autoY ? fieldText(view.yMin) : yMinText;
  const shownYMax = autoY ? fieldText(view.yMax) : yMaxText;

  return (
    <>
      <div className="space-y-3">
        {rows.map((row, index) => {
          const problem = rowProblem(parsed[index], strings);
          return (
            <div key={row.id}>
              <div className="flex items-start gap-2">
                <span
                  aria-hidden="true"
                  className="mt-9 inline-block h-4 w-4 shrink-0 rounded-sm"
                  style={{
                    backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                  }}
                />
                <div className="min-w-0 flex-1">
                  <TextField
                    hint={index === 0 ? strings.rowHint : undefined}
                    id={`gc-row-${row.id}`}
                    label={t(strings.rowLabel, { n: index + 1 })}
                    mono
                    onChange={(value) => updateRow(row.id, value)}
                    placeholder={strings.rowPlaceholder}
                    value={row.text}
                  />
                </div>
                {rows.length > 1 ? (
                  <button
                    aria-label={t(strings.removeFunction, { n: index + 1 })}
                    className={`${BUTTON_SMALL} mt-7`}
                    onClick={() => removeRow(row.id)}
                    type="button"
                  >
                    ×
                  </button>
                ) : null}
              </div>
              {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
            </div>
          );
        })}
        <div className="flex flex-wrap items-center gap-3">
          <button
            className={BUTTON_PRIMARY}
            disabled={rows.length >= MAX_ROWS}
            onClick={addRow}
            type="button"
          >
            {strings.addFunction}
          </button>
          {rows.length >= MAX_ROWS ? (
            <span className="text-muted-foreground text-sm">
              {t(strings.maxFunctions, { max: MAX_ROWS })}
            </span>
          ) : null}
        </div>
      </div>
      <fieldset className="space-y-3">
        <legend className="font-medium text-foreground text-sm">
          {strings.windowHeading}
        </legend>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <TextField
            id="gc-xmin"
            label={strings.xMinLabel}
            mono
            numeric
            onChange={setXMinText}
            value={xMinText}
          />
          <TextField
            id="gc-xmax"
            label={strings.xMaxLabel}
            mono
            numeric
            onChange={setXMaxText}
            value={xMaxText}
          />
          <TextField
            id="gc-ymin"
            label={strings.yMinLabel}
            mono
            numeric
            onChange={(value) => {
              setAutoY(false);
              setYMinText(value);
            }}
            value={shownYMin}
          />
          <TextField
            id="gc-ymax"
            label={strings.yMaxLabel}
            mono
            numeric
            onChange={(value) => {
              setAutoY(false);
              setYMaxText(value);
            }}
            value={shownYMax}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {(
            [
              [strings.zoomIn, () => zoom(0.5)],
              [strings.zoomOut, () => zoom(2)],
              [strings.panLeft, () => pan(-0.25, 0)],
              [strings.panRight, () => pan(0.25, 0)],
              [strings.panUp, () => pan(0, 0.25)],
              [strings.panDown, () => pan(0, -0.25)],
              [strings.reset, reset],
            ] as const
          ).map(([label, action]) => (
            <button
              className={BUTTON_SMALL}
              key={label}
              onClick={action}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox checked={autoY} label={strings.autoY} onChange={setAutoY} />
          <Checkbox
            checked={square}
            label={strings.square}
            onChange={setSquare}
          />
        </div>
        {typedX ? null : <ErrorNotice>{strings.windowInvalid}</ErrorNotice>}
      </fieldset>
      <fieldset className="space-y-2">
        <legend className="font-medium text-foreground text-sm">
          {strings.features}
        </legend>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={zeros}
            label={strings.featureZeros}
            onChange={setZeros}
          />
          <Checkbox
            checked={crossings}
            label={strings.featureCrossings}
            onChange={setCrossings}
          />
          <Checkbox
            checked={turning}
            label={strings.featureTurning}
            onChange={setTurning}
          />
        </div>
      </fieldset>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <GraphingView
          fmt={fmt}
          functions={functions}
          setTableStart={setTableStart}
          setTableStep={setTableStep}
          strings={strings}
          switches={{ zeros, crossings, turning }}
          tableStart={tableStart}
          tableStep={tableStep}
          window={view}
        />
      </section>
    </>
  );
}
